import { useCallback, useEffect, useMemo, useRef } from 'react';

import { DirectUpload } from '@rails/activestorage';

import { api } from '@api/reduxApi';
import { useBulkUploadState } from '@components/BulkUploadFlow';
import { track } from '@components/tracking';

import { startMuxUpload } from '../utils/startMuxUpload';

const DEFAULT_NUM_UPLOADS_IN_PROGRESS_LIMIT = 6;

type Options = {
  numUploadsInProgressLimit?: number;
};

export const useUploadFiles = (
  items: DirectlyUploadableItem[],
  dispatchSetItem: (item: Patch<DirectlyUploadableItem>) => void,
  opts?: Options
): void => {
  const [updateRepoSession, {}] = api.useUpdateRepoSessionMutation();
  const [getMuxDirectUploadUrl] = api.useGetMuxDirectUploadUrlMutation();
  const [createRecording] = api.useCreateRecordingMutation();
  const [createInsight] = api.useCreateInsightMutation();

  const handleProgress = (itemId: number, progress: number) => dispatchSetItem({ id: itemId, progress });
  const handleError = (itemId: number) => dispatchSetItem({ id: itemId, status: 'errored' });

  const { studyId } = useBulkUploadState();

  const uploadRecording = useCallback(
    async (item: RecordingItem) => {
      const { url: endpoint, passthrough: mux_passthrough } = await getMuxDirectUploadUrl().unwrap();

      const abort = await startMuxUpload(item, endpoint, {
        onProgress: (progress) => handleProgress(item.id, progress),
        onError: () => handleError(item.id),
        onSuccess: async () => {
          const { name: filename, size: byte_size } = item.file;
          const recording = await createRecording({ mux_passthrough, filename, byte_size }).unwrap();

          updateRepoSession({ uuid: item.session.uuid, recording_id: recording.id })
            .unwrap()
            .then((data) => {
              dispatchSetItem({ id: item.id, status: 'done', session: data });
            });
          track('added_recording', { recording, method: 'manual' });
        }
      });

      const cancelUpload = () => {
        dispatchSetItem({ id: item.id, status: 'canceled' });
        abort();
      };

      dispatchSetItem({ id: item.id, status: 'uploading', progress: 0, cancelUpload });
    },
    [getMuxDirectUploadUrl, createRecording, updateRepoSession, dispatchSetItem]
  );

  const uploadRepoFile = useCallback(
    (item: RepoFileItem) => {
      new DirectUpload(item.file, '/rails/active_storage/direct_uploads', {
        directUploadWillStoreFileWithXHR: (request) => {
          const cancelUpload = () => {
            dispatchSetItem({ id: item.id, status: 'canceled' });
            request.abort();
          };

          request.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              handleProgress(item.id, Math.round((event.loaded * 100) / event.total));
            }
          });

          dispatchSetItem({ id: item.id, status: 'uploading', progress: 0, cancelUpload });
        }
      }).create(async (error, blob) => {
        if (error) {
          handleError(item.id);
        } else {
          const insight = await createInsight({
            content_type: 'repo_file',
            signed_id: blob.signed_id,
            title: item.file.name,
            project_id: studyId
          }).unwrap();
          dispatchSetItem({ id: item.id, insight, status: 'done' });
          track('added_repo_file', { id: insight.id });
        }
      });
    },
    [createInsight, dispatchSetItem]
  );

  const processFile = useCallback(
    (item: DirectlyUploadableItem) => {
      switch (item.type) {
        case 'recording':
          return uploadRecording(item);
        case 'repoFile':
          return uploadRepoFile(item);
      }
    },
    [uploadRecording, uploadRepoFile]
  );

  const { inProgressItems, queuedItems } = useMemo(
    () => ({
      inProgressItems: items.filter((item) => item.status === 'uploading'),
      queuedItems: items.filter((item) => item.status === 'queued')
    }),
    [items]
  );

  const numUploadsInProgressLimit = opts?.numUploadsInProgressLimit || DEFAULT_NUM_UPLOADS_IN_PROGRESS_LIMIT;
  const startedIds = useRef(new Set<number>());

  useEffect(() => {
    if (inProgressItems.length >= numUploadsInProgressLimit || queuedItems.length === 0) return;

    const nextItem = queuedItems.find((item) => !startedIds.current.has(item.id));
    if (!nextItem) return;

    startedIds.current.add(nextItem.id);
    processFile(nextItem);
  }, [inProgressItems.length, queuedItems.length, processFile, numUploadsInProgressLimit]);
};
